// ============================================================================
// Rota · contratos v1 · 21/10/2026
// ============================================================================
export const CONTRACT_VERSION = 1 as const;

// ---------- Primitivos ----------
export type Ms = number;          // epoch ms UTC
export type DateKey = string;     // 'YYYY-MM-DD' em America/Sao_Paulo
export type Cents = number;       // inteiro, centavos de BRL
export type Rate = number;        // fração 0..1
export type Id = string;
export type Unsubscribe = () => void;

// ---------- Fontes e frescor ----------
export type DataSource = 'meta' | 'vturb' | 'kiwify' | 'tracker' | 'rules' | 'system';
export type IntegrationSource = 'meta' | 'vturb' | 'kiwify' | 'tracker';
export type LatencyClass = 'realtime' | 'near_realtime' | 'delayed';

export interface Freshness {
  source: DataSource;
  latency: LatencyClass;
  fetchedAt: Ms;        // quando o backend/mocks obteve
  dataAsOf: Ms;         // até quando o dado vale (Meta: último refresh de 15 min)
  stale: boolean;       // (agora − dataAsOf) > SLA da fonte (3.5). Idade do DADO, não da consulta
}

/** Rejeição padrão de qualquer método do DataProvider (7.3). */
export interface ProviderError {
  code: 'forbidden' | 'not_found' | 'conflict' | 'rate_limited' | 'source_unavailable' | 'invalid' | 'internal';
  message: string; retryAfterMs?: number;
  conflict?: { by: string; at: Ms };     // ex.: decisão já aplicada por outra pessoa
}

export type IntegrationState = 'connected' | 'degraded' | 'error' | 'disconnected' | 'not_configured';
export interface IntegrationStatus {
  source: IntegrationSource;
  state: IntegrationState;
  label: string;                         // "Meta Ads · 2 contas" (detalhe por conta em `accounts`)
  accounts?: { id: Id; name: string; state: IntegrationState; timezoneOk: boolean }[];  // só Meta
  latency: LatencyClass;
  lastSyncAt: Ms | null;
  lagMs: number | null;                  // agora − dataAsOf
  slaMs: number;                         // 3.5
  eventsLastHour: number | null;
  eventsSpark?: number[];                // 12 baldes de 5 min
  quota?: { used: number; limit: number; window: 'minute' | 'hour'; resetsAt: Ms };
  lastError?: { at: Ms; code: string; message: string };
}

// ---------- Papéis e permissões ----------
export type Role = 'owner' | 'partner' | 'traffic' | 'creative' | 'support' | 'expert' | 'tech' | 'tv';
export type Permission =
  | 'finance:read' | 'finance:costs' | 'pii:read' | 'pii:reveal' | 'ads:act' | 'decisions:act'
  | 'rules:write' | 'goals:write' | 'recovery:work' | 'users:manage' | 'export:csv'
  | 'settings:write' | 'sim:control';
export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  owner:   ['finance:read','finance:costs','pii:read','pii:reveal','ads:act','decisions:act','rules:write',
            'goals:write','recovery:work','users:manage','export:csv','settings:write','sim:control'],
  partner: ['finance:read','pii:read','decisions:act','goals:write','export:csv'],
  traffic: ['ads:act','decisions:act','rules:write','export:csv'],
  creative:['decisions:act'],
  support: ['pii:read','pii:reveal','decisions:act','recovery:work'],
  expert:  ['finance:read','decisions:act'],
  tech:    ['decisions:act','rules:write','settings:write','sim:control'],
  tv:      [],
};
export type OwnerTag = 'GT' | 'SR' | 'CE' | 'EX' | 'SO' | 'TEC';
export const OWNER_ROLES: Record<OwnerTag, readonly Role[]> = {
  GT: ['traffic'], SR: ['support'], CE: ['creative'], EX: ['expert'], SO: ['owner','partner'], TEC: ['tech'],
};
export interface Viewer { id: Id; name: string; initials: string; role: Role; permissions: readonly Permission[] }

// ---------- Período, comparação, filtros ----------
export type PeriodPreset = 'today' | 'yesterday' | '7d' | '30d' | '90d' | 'custom';
export interface DateRange { preset: PeriodPreset; from: DateKey; to: DateKey }   // inclusivo
export type CompareMode = 'yesterday_same_time' | 'same_weekday' | 'avg_4w_same_weekday'
  | 'previous_period' | 'previous_month' | 'none';
export type Device = 'mobile' | 'desktop' | 'tablet';
export type Placement = 'feed' | 'stories' | 'reels' | 'other';
export interface Filters {
  accountIds?: Id[];
  campaignIds?: Id[]; adsetIds?: Id[]; adIds?: Id[];
  placements?: Placement[]; devices?: Device[];
  paymentMethods?: PaymentMethod[]; products?: ProductKind[];
  utmSources?: string[];
  quiz?: { questionId: QuizQuestionId; optionIds: string[] }[];
}
export interface Query {
  range: DateRange;
  compare: CompareMode;
  filters: Filters;
  asOf?: Ms;            // Replay do dia (3.5); ausente = agora
}

// ---------- KPIs ----------
export type KpiId =
  // mídia
  | 'spendAm' | 'mediaCost' | 'impressions' | 'cpm' | 'linkClicks' | 'ctrLink' | 'cpcLink' | 'lpv'
  | 'connectRate' | 'connectReal' | 'frequency7d' | 'hookRate' | 'holdRate' | 'creativeAgeDays' | 'oldCreativeSpendShare'
  | 'winnersActive' | 'spendPacing'
  // quiz
  | 'quizViews' | 'quizStartRate' | 'quizCompletionRate' | 'quizMedianTimeSec' | 'quizToVsl'
  // vsl
  | 'vslLive' | 'vslViews' | 'vslPlayRate' | 'vslEngagement' | 'pitchRetention' | 'postPitchCtr' | 'vslPageConv'
  // checkout e pagamento
  | 'initiateCheckouts' | 'icRate' | 'cpi' | 'checkoutConv' | 'cardApprovalAttempt' | 'cardApprovalBuyer'
  | 'pixPaidRate' | 'pixMedianMin' | 'boletoPaidRate' | 'pendingRevenue' | 'pendingExpected'
  // receita e lucro
  | 'revenueGross' | 'revenueNet' | 'interestPaid' | 'frontSales' | 'upsellSales' | 'revenueFinal'
  | 'cpa' | 'cpaBlended' | 'roas' | 'roasFront' | 'roasReal' | 'mer' | 'profit' | 'profitOperating'
  | 'margin' | 'roi' | 'aov' | 'rpv' | 'epc'
  // upsell e pós-venda
  | 'upsellTakeRate' | 'upsellRevenuePerBuyer' | 'refundRateD7' | 'refundRateD30' | 'refundRateD60'
  | 'chargebackRateMonth' | 'revenueAtRisk' | 'projectedRefunds' | 'cashAvailable' | 'cashPending' | 'runwayDays'
  | 'refundsAmount' | 'refundsCount'                      // ADR-002: reembolsos e chargebacks no período (por evento)
  // recuperação
  | 'cartsAbandoned' | 'cartAbandonRate' | 'cartRecoveryRate' | 'recoveredRevenue' | 'recoveredCount'
  | 'recoveryRate72h'                                     // ADR-002: taxa geral da fila em 72 h (4.9, REC-01)
  | 'contactSlaMedianMin' | 'queueOverdue' | 'moneyAtStake'
  // rastreamento
  | 'utmCoverage' | 'sckCoverage' | 'untrackedSales' | 'webhookLagP95Sec' | 'pixelKiwifyDivergence'
  // metas e ritmo
  | 'goalRevenueDay' | 'goalSalesDay' | 'goalSpendDay' | 'paceRevenue' | 'projectionRevenueEod'
  | 'goalProgressRevenue' | 'goalProgressProfit' | 'goalProgressSales'
  // parâmetros derivados
  | 'cpaBreakEven' | 'cpaTarget' | 'roasBreakEven' | 'roasTarget' | 'cpiTarget' | 'roasRealTarget'
  | 'merBreakEven' | 'merTarget' | 'roiTarget' | 'rpvBreakEven';

export type KpiFormat = 'brl0' | 'brl2' | 'brlCompact' | 'int' | 'pct1' | 'pct2' | 'pp' | 'ratio2' | 'mult1'
  | 'mmss' | 'min' | 'days' | 'sec';
export type Polarity = 'higher_better' | 'lower_better' | 'neutral' | 'band';
export type KpiStatus = 'good' | 'warn' | 'bad' | 'neutral' | 'insufficient';
export type ParamRef = 'cpaTarget' | 'cpaBreakEven' | 'roasTarget' | 'roasBreakEven' | 'cpcBreakEven'
  | 'cpiTarget' | 'roasRealTarget' | 'merBreakEven' | 'merTarget' | 'roiTarget' | 'rpvBreakEven'
  | 'targetMargin' | 'goal';

export type ThresholdSpec =
  | { kind: 'none' }
  | { kind: 'static'; good: number; bad: number }                       // V ≥ good / R < bad (ou inverso por polaridade)
  | { kind: 'band'; goodMin: number; goodMax: number; warnMin: number; warnMax: number }
  | { kind: 'param'; good: ParamRef; bad: ParamRef; goodMult?: number; badMult?: number }
  | { kind: 'baseline'; baseline: 'median14d' | 'same_hour_4w' | 'own30d'; warnDelta: number; badDelta: number;
      deltaKind: 'relative' | 'pp' };

export type MetricCategory = 'midia' | 'quiz' | 'vsl' | 'checkout' | 'receita' | 'posvenda' | 'recuperacao'
  | 'rastreamento' | 'metas';

export interface KpiDef {
  id: KpiId;
  label: string;            // pt-BR, ex. "Conversão do checkout"
  short?: string;           // ex. "Checkout"
  help: string;             // 1–2 frases
  formula: string;          // legível, ex. "vendas do curso ÷ checkouts iniciados"
  sources: DataSource[];
  format: KpiFormat;
  polarity: Polarity;
  thresholds: ThresholdSpec;
  minSample?: { unit: string; n: number };   // "plays", 368
  estimated?: boolean;      // limiar [E]
  requires?: Permission;    // ex. 'finance:read'
  category: MetricCategory;
}

export interface KpiValue {
  id: KpiId;
  value: number | null;               // null = indisponível nesta quebra/fonte
  compareValue: number | null;
  delta: number | null;               // relativo (fração) ou pp para taxas — ver deltaKind
  deltaKind: 'relative' | 'pp' | 'abs';
  status: KpiStatus;
  sample?: { n: number; min: number; unit: string };
  spark?: (number | null)[];          // série curta (hoje por hora ou 7 d)
  sparkCompare?: (number | null)[];
  freshness: Freshness[];
  note?: string;                      // ex. "inclui 3 vendas não trackeadas"
  /** ADR-002: a referência do limiar `baseline` (mediana de 14 dias, mesma faixa de 4 semanas ou 30 dias
   *  do próprio objeto), na mesma unidade do valor. Ausente quando o limiar não é de baseline. */
  baseline?: number | null;
}

// ---------- Metas e ritmo ----------
export type PaceState = 'ahead' | 'on_track' | 'behind';
export interface GoalProgress {
  dateKey: DateKey;
  revenue: { goal: Cents; actual: Cents; expectedNow: Cents; pace: number; state: PaceState };
  profit:  { goal: Cents; actual: Cents; expectedNow: Cents; pace: number; state: PaceState } | null; // null sem finance:read
  sales:   { goal: number; actual: number; expectedNow: number; pace: number; state: PaceState };
  projection: { revenue: Cents; low: Cents; high: Cents; profit: Cents | null };
  fNow: Rate;                         // F(h) agora
  nextCheckpoint: { at: Ms; label: string };
}

// ---------- Meta Ads ----------
export type MetaConfiguredStatus = 'ACTIVE' | 'PAUSED' | 'DELETED' | 'ARCHIVED';
export type MetaEffectiveStatus = MetaConfiguredStatus | 'IN_PROCESS' | 'WITH_ISSUES' | 'CAMPAIGN_PAUSED'
  | 'ADSET_PAUSED' | 'PENDING_REVIEW' | 'DISAPPROVED' | 'PREAPPROVED' | 'PENDING_BILLING_INFO';
export interface MetaIssue { code?: number; summary: string; level?: 'AD' | 'ADSET' | 'CAMPAIGN' | 'ACCOUNT' }
export interface EditLogEntry { at: Ms; by: string; field: 'status' | 'daily_budget'; from: string; to: string; source: 'rota' | 'ads_manager' }

export interface AdAccount {
  id: Id; name: string; currency: 'BRL'; timezone: string;   // deve ser America/Sao_Paulo; se não for, aviso em TRK-02
  /** Mapeado do `account_status` numérico da Meta: 1 ACTIVE · 2 DISABLED · 3 UNSETTLED · 7 PENDING_RISK_REVIEW
   *  · 8 PENDING_SETTLEMENT · 9 IN_GRACE_PERIOD · 100 PENDING_CLOSURE · 101 CLOSED. */
  status: 'ACTIVE' | 'DISABLED' | 'UNSETTLED' | 'PENDING_RISK_REVIEW' | 'PENDING_SETTLEMENT' | 'IN_GRACE_PERIOD'
    | 'PENDING_CLOSURE' | 'CLOSED';
  spendCap: Cents | null;
  colorSlot: 1 | 2 | 3 | 4 | 5 | 6;
}
export interface Campaign {
  id: Id; accountId: Id; name: string; objective: string;
  status: MetaConfiguredStatus; effectiveStatus: MetaEffectiveStatus;
  isCbo: boolean; dailyBudget: Cents | null; lifetimeBudget: Cents | null;
  bidStrategy: 'LOWEST_COST_WITHOUT_CAP' | 'COST_CAP' | 'LOWEST_COST_WITH_BID_CAP' | 'LOWEST_COST_WITH_MIN_ROAS';
  colorSlot: 1 | 2 | 3 | 4 | 5 | 6;     // cor fixa por entidade (9.6)
  issues: MetaIssue[]; createdAt: Ms; edits: EditLogEntry[];
}
export interface AdSet {
  id: Id; campaignId: Id; accountId: Id; name: string;
  status: MetaConfiguredStatus; effectiveStatus: MetaEffectiveStatus;
  dailyBudget: Cents | null; optimizationGoal: string;
  learning: { status: 'LEARNING' | 'SUCCESS' | 'FAIL' | 'UNKNOWN'; events7d: number; lastSignificantEditAt: Ms | null };
  audienceType: 'cold' | 'retargeting';
  budgetChangesLastHour: number; lastEditAt: Ms | null;
  issues: MetaIssue[]; createdAt: Ms; edits: EditLogEntry[];
}
export interface Creative {
  id: Id; code: string;                    // "V07"
  format: 'video' | 'image' | 'carousel';
  thumbnailUrl: string;                    // V1: /mock/thumbs/v07.webp (gerado); fase 2: cópia no VPS
  title?: string; body?: string; videoDurationSec?: number;
  firstImpressionAt: Ms;
}
export interface Ad {
  id: Id; adsetId: Id; campaignId: Id; accountId: Id; name: string;
  status: MetaConfiguredStatus; effectiveStatus: MetaEffectiveStatus;
  creative: Creative; reviewFeedback?: string; issues: MetaIssue[]; edits: EditLogEntry[];
}
export type InsightLevel = 'account' | 'campaign' | 'adset' | 'ad';

/** Linha da tabela de Tráfego: Meta + Kiwify + tracker + VTurb já cruzados no provider. */
export interface InsightRow {
  level: InsightLevel; id: Id; name: string;
  accountId: Id; campaignId?: Id; adsetId?: Id; adId?: Id;   // campaignId ausente só no nível 'account'
  status: MetaEffectiveStatus; dailyBudget: Cents | null;
  budgetOwner: 'self' | 'campaign' | 'adset' | null;          // onde vive o orçamento (4.3); null no anúncio
  badges: ('learning' | 'fatigue' | 'disapproved' | 'new' | 'pinned')[];
  thumbnailUrl?: string;
  // mídia
  spendAm: Cents; mediaCost: Cents; impressions: number; reach: number | null; frequency: number | null;
  cpm: Cents | null; linkClicks: number; ctrLink: Rate | null; cpcLink: Cents | null; lpv: number | null;
  connectRate: Rate | null; hookRate: Rate | null; holdRate: Rate | null; creativeAgeDays: number | null;
  // funil próprio (via UTM ids → sessões)
  quizStarts: number; quizCompletes: number; vslPlays: number; pitchReached: number; ctaClicks: number;
  initiateCheckouts: number;
  // resultado (Kiwify, atribuído)
  frontSales: number; upsellSales: number; revenueGross: Cents; revenueNet: Cents;
  cpa: Cents | null; roas: number | null; profit: Cents | null;   // profit null sem finance:read
  margin: Rate | null; roi: Rate | null; roasReal: number | null;
  pitchRetention: Rate | null; postPitchCtr: Rate | null; checkoutConv: Rate | null; upsellTakeRate: Rate | null;
  refundRateD30: Rate | null;
  /**
   * ADR-002: coorte de reembolso em 30 dias, independente do período. ADR-004: vendas de `from` a
   * `to` (60 a 7 dias atrás); `rate` = `refunds ÷ exposure`, em que `exposure` soma a fração dos
   * reembolsos de 30 dias que a idade de cada venda já deixa ver (1 para vendas com 30 dias ou mais).
   * Sem `exposure` (provider antigo), `rate` = `refunds ÷ sales` da coorte madura (30 a 60 dias).
   */
  refundCohortD30?: { rate: Rate | null; sales: number; refunds: number; from: DateKey; to: DateKey;
    exposure?: number };
  metaPurchases: number | null;          // pixel (diagnóstico)
  cpaSpark7d: (number | null)[];
  status_: Partial<Record<KpiId, KpiStatus>>;   // status por coluna, já calculado
  freshness: Freshness[];
}
export interface InsightQuery extends Query {
  level: InsightLevel; parentId?: Id; search?: string; onlyActive?: boolean;
  sort: { key: keyof InsightRow; dir: 'asc' | 'desc' }; offset: number; limit: number;
}
export interface InsightTable { rows: InsightRow[]; total: InsightRow; count: number; untrackedSales: number }

// ---------- Quiz ----------
export type QuizQuestionId = 'q1_objetivo' | 'q2_companhia' | 'q3_idade' | 'q4_formacao' | 'q5_trabalho'
  | 'q6_ingles' | 'q7_passaporte' | 'q8_reserva' | 'q9_prazo' | 'q10_obstaculo' | 'q11_genero';
export interface QuizOption { id: string; label: string }
export interface QuizQuestion { id: QuizQuestionId; step: number; short: string; text: string; options: QuizOption[] }
export interface QuizStepStat {
  questionId: QuizQuestionId; step: number; reached: number; answered: number;
  drop: Rate; medianSec: number; p75Sec: number; leaking: boolean;
}
export interface QuizReport {
  questions: QuizQuestion[];
  views: number; starts: number; completes: number; toVsl: number;
  steps: QuizStepStat[];
  resultFunnel: { resultViews: number; ctaClicks: number; vslLoaded: number; medianSecToVsl: number };
  freshness: Freshness[];
}
export interface SegmentCell {
  questionId: QuizQuestionId; optionId: string; label: string;
  share: Rate; completes: number; sales: number;
  convRate: Rate | null; lift: number | null; ci90: [number, number] | null;
  upsellRate: Rate | null; refundRateD30: Rate | null; rpv: Cents | null;
  sufficient: boolean;
  ci95?: [number, number] | null;          // ADR-002: IC de Wilson 95% do lift (R41 usa o de 95%)
}
export interface SegmentQuery extends Query { dims: QuizQuestionId[]; metric: 'conv' | 'rpv' | 'upsell' | 'refund' }
export interface SegmentReport {
  cells: SegmentCell[];                    // 1 dimensão
  cross?: { rows: string[]; cols: string[]; values: (number | null)[][]; sufficient: boolean[][] }; // 2 dimensões
  buyerOfTheDay: { sentence: string; traits: { questionId: QuizQuestionId; optionId: string; label: string; lift: number }[];
    window?: 'today' | 'last7d' } | null;   // ADR-002: janela de onde saiu o retrato
  angles: { id: Id; title: string; lift: number; ci90: [number, number]; sales: number; ruleDecisionId?: Id;
    questionId?: QuizQuestionId; optionId?: string;     // ADR-002: célula de origem
    ci95?: [number, number];                            // ADR-002: IC de Wilson 95% (o critério da R41)
    completes?: number }[];
}

// ---------- VSL (VTurb + tracker) ----------
export type PlayerRole = 'vsl_main' | 'upsell';
export interface VslPlayer { id: Id; role: PlayerRole; name: string; durationSec: number; pitchSec: number;
  chapters: { atSec: number; label: string }[] }
export interface RetentionPoint { second: number; pct: Rate; reached: number; clicks?: number; conversions?: number }
export interface RetentionCurve {
  playerId: Id; stepSec: 5; totalPlays: number; pitchSec: number; pitchReachedPct: Rate;
  segment?: { key: 'utm_content' | 'device' | 'all'; value: string; label: string; colorSlot?: number };
  points: RetentionPoint[];
  compare?: RetentionPoint[];
  freshness: Freshness[];
}
export interface RetentionQuery extends Query { playerRole: PlayerRole; segmentBy: 'all' | 'creative' | 'device'; topN?: number }
export interface VslSourceRow { key: string; label: string; adId?: Id; plays: number; playRate: Rate; pitchRetention: Rate;
  postPitchCtr: Rate | null; conv: Rate | null; rpv: Cents | null; status_: Partial<Record<KpiId, KpiStatus>> }
export interface AbTest { id: Id; name: string; variants: { label: string; plays: number; pitchRetention: Rate; conv: Rate; rpv: Cents;
    sales?: number }[];                    // ADR-002: vendas medidas da variante (não mais conv × plays)
  verdict: 'a_wins' | 'b_wins' | 'inconclusive'; confidence: Rate;
  startedAt?: Ms;                          // ADR-002: início do teste (ETA "≈ N dias no ritmo atual")
  rpvBasis?: 'front' | 'front_upsell' }    // ADR-002: o que entra no faturamento por play

// ---------- Kiwify: pedidos, carrinhos, reembolsos ----------
export type PaymentMethod = 'credit_card' | 'pix' | 'boleto';
/** `premium` = Programa América Premium (consulta individual, US$ 397): produto próprio, nunca conta como 20X. */
export type ProductKind = 'front' | 'upsell' | 'premium';
export type OrderStatus = 'waiting_payment' | 'paid' | 'refused' | 'refunded' | 'chargedback';
export type AttributionMethod = 'utm_ids' | 'sck_session' | 'utm_names' | 'manual' | 'unattributed';
export interface TrackingParams {
  utm_source?: string | null; utm_medium?: string | null; utm_campaign?: string | null;
  utm_content?: string | null; utm_term?: string | null;
  src?: string | null;   // reservado à Conversion Key da VTurb (v3_...)
  sck?: string | null;   // funnel session id (sid)
  s1?: string | null; s2?: string | null; s3?: string | null;
}
export interface AttributionRef {
  method: AttributionMethod; campaignId?: Id; adsetId?: Id; adId?: Id; placement?: Placement;
  reason?: 'utm_empty' | 'utm_no_ids' | 'non_meta_source' | 'sck_missing';   // quando unattributed
}
/** Cliente como a UI recebe. Sem pii:read => null. */
export interface MaskedCustomer { displayName: string; emailMasked: string; phoneMasked: string | null; hasDocument: boolean }
export interface CustomerPii { name: string; email: string; phone: string | null; document: string | null }
export interface Order {
  id: Id; ref: string; product: ProductKind; productName: string;
  status: OrderStatus; paymentMethod: PaymentMethod; installments: number | null;
  cardRejectionReason?: string | null;
  amounts: { basePrice: Cents; discountPct: Rate; gross: Cents /* basePrice × (1 − discountPct): entra no faturamento */;
    charged: Cents; interest: Cents /* max(0, charged − gross) */; fee: Cents; net: Cents;
    /** Repasse aos coprodutores (só informação; a DRE do modo real não desconta). Opcional. */
    coproduction?: Cents };
  /** Mesma pessoa em vários pedidos (pedaço do hash do e-mail, sem dado pessoal). Opcional. */
  buyerKey?: string;
  parentOrderId?: Id | null;            // upsell → pedido do curso
  /** A API de vendas tem status que o webhook não tem (refund_requested, pending_refund, processing, authorized).
   *  A reconciliação mapeia: refund_requested/pending_refund → status 'paid' + refundRequestedAt (entra em "em risco");
   *  processing/authorized/pending → 'waiting_payment'; approved → 'paid'. */
  refundRequestedAt?: Ms | null;
  customer: MaskedCustomer | null;
  tracking: TrackingParams; attribution: AttributionRef; sessionId: Id | null;
  quizTraits?: { questionId: QuizQuestionId; optionId: string; label: string }[];  // se houver sessão
  createdAt: Ms; approvedAt: Ms | null; refundedAt: Ms | null; pixExpiresAt?: Ms | null;
  expectedDepositAt?: Ms | null;
}
export interface OrderJourney {
  order: Order; upsellOrder?: Order | null;
  steps: { at: Ms; kind: 'ad' | 'quiz_start' | 'quiz_complete' | 'vsl_play' | 'vsl_pitch' | 'cta' | 'checkout'
    | 'pix_created' | 'paid' | 'refused' | 'upsell_view' | 'upsell_paid' | 'upsell_declined' | 'refunded';
    label: string; detail?: string }[];
  quizAnswers?: { questionId: QuizQuestionId; label: string; answer: string }[];
  vsl?: { maxSecond: number; reachedPitch: boolean };
  ad?: { id: Id; name: string; thumbnailUrl: string };
}
export interface Refund {
  id: Id; orderId: Id; kind: 'refund' | 'chargeback'; amount: Cents; product: ProductKind;
  paymentMethod: PaymentMethod; purchasedAt: Ms; refundedAt: Ms; daysAfterPurchase: number;
  reason: string | null; attribution: AttributionRef;
}

// ---------- Recuperação ----------
export type RecoveryType = 'refused' | 'pix' | 'cart' | 'boleto';
export type RecoveryStatus = 'open' | 'contacted' | 'recovered' | 'lost' | 'paid_alone' | 'expired';
export type RecoveryChannel = 'whatsapp' | 'phone' | 'email' | 'kiwify_agent';
export type ObjectionTag = 'preco' | 'ingles' | 'medo' | 'tempo' | 'conjuge' | 'confianca' | 'outro';
export interface RecoveryItem {
  id: Id; type: RecoveryType; status: RecoveryStatus;
  mergedTypes: RecoveryType[];             // "uma pessoa, um item" (4.9): ex. ['refused','pix']
  agentActiveUntil?: Ms | null;            // Agente Kiwify em contato até (4.9)
  orderId?: Id; cartId?: Id; product: ProductKind; value: Cents;
  customer: MaskedCustomer | null;
  createdAt: Ms; dueAt: Ms; closesAt: Ms;
  assigneeId: Id | null; contacts: { at: Ms; by: Id; channel: RecoveryChannel; note?: string }[];
  outcome?: { at: Ms; by: Id | 'system'; result: 'recovered' | 'lost' | 'paid_alone' | 'expired';
    recoveredOrderId?: Id; objection?: ObjectionTag; note?: string;
    forPauta?: boolean };                  // ADR-002: o suporte liberou a nota para a Pauta (EXP-03)
  traits: { label: string }[];             // 2 chips do quiz (objetivo, obstáculo)
  originLabel: string;                     // "TC-22 · V33"
  payLink?: string;                        // checkout_link ou código Pix (só com pii:read)
  messageTemplateId: string;               // 9.11
}
export type RecoveryCommand =
  | { kind: 'assign'; itemId: Id; assigneeId: Id | null }
  | { kind: 'contacted'; itemId: Id; channel: RecoveryChannel; note?: string }
  | { kind: 'close'; itemId: Id; result: 'recovered' | 'lost'; objection?: ObjectionTag; note?: string;
      shareNoteWithExpert?: boolean };   // só notas marcadas vão para EXP-03, depois do filtro de PII
/** ADR-002 · EXP-03 (4.13): objeções da recuperação já agregadas e sem dado pessoal. */
export interface PautaObjections {
  since: Ms; until: Ms;
  total: number;                           // itens perdidos com objeção na janela
  rows: { tag: ObjectionTag; count: number; share: Rate }[];   // mais marcada primeiro
  /** Até 2 notas liberadas pelo suporte (`outcome.forPauta`), filtradas no provider (e-mail, telefone,
   *  CPF e nome do cliente fora), da objeção mais marcada primeiro. Vazio para o papel `tv`. */
  examples: { id: Id; tag: ObjectionTag; text: string; at: Ms }[];
}

// ---------- Funil ----------
export type FunnelStepId = 'impressions' | 'linkClicks' | 'lpv' | 'quizStart' | 'quizComplete' | 'vslView'
  | 'vslPlay' | 'pitch' | 'ctaClick' | 'checkout' | 'frontSale' | 'upsellView' | 'upsellSale' | 'members';
export interface FunnelStep {
  id: FunnelStepId; label: string; source: DataSource; value: number;
  compareValue: number | null;
  rateFromPrev: Rate | null; rateFromTop: Rate | null;
  rateKpi?: KpiId;                          // taxa que julga a etapa
  status: KpiStatus; deviationVsBaseline: number | null;   // Δln
  worst: boolean;
}
/** ADR-002: amostra de um segmento do funil (dispositivo ou posicionamento) para o "mínimo" da UI. */
export interface FunnelSegmentSample { clicks: number; quizStarts: number; quizCompletes: number; vslPlays: number;
  pitchReached: number; checkouts: number; sales: number }
export interface FunnelReport {
  steps: FunnelStep[]; cascade: CpaCascade;
  byDevice: Record<Device, Partial<Record<KpiId, number | null>>>;
  byPlacement: Record<Placement, Partial<Record<KpiId, number | null>>>;
  /** ADR-002: amostra por segmento (a UI esconde taxas sem amostra). */
  sampleByDevice?: Record<Device, FunnelSegmentSample>;
  sampleByPlacement?: Record<Placement, FunnelSegmentSample>;
}
export interface CpaCascade {
  cpaNow: Cents | null; cpaBaseline: Cents | null;
  factors: { id: 'cpm' | 'ctr' | FunnelStepId; label: string; deltaLn: number; impactCents: Cents }[];
  probableCause: { factorId: string; sentence: string; owner: OwnerTag; route: string } | null;
}

// ---------- Séries ----------
export type SeriesKey =
  | 'revenueCumulative' | 'revenueByHour' | 'salesByHour' | 'upsellByHour' | 'spendByHour' | 'cpaByHour' | 'cpaCumulative'
  | 'profitCumulative' | 'revenueByDay' | 'spendByDay' | 'profitByDay' | 'funnelRateByHour'
  | 'kpiByHour' | 'kpiByDay' | 'heatmapHourWeekday' | 'cashCalendar' | 'refundCohorts' | 'atRiskByWeek';
export interface SeriesQuery extends Query {
  key: SeriesKey; kpi?: KpiId; bucket: '5m' | '1h' | '1d' | '1w';
  splitBy?: 'campaign' | 'product' | 'method' | 'none'; entityId?: Id; withProjection?: boolean;
}
/** Colunar, pronto para uPlot. t em ms. */
export interface ColumnarSeries {
  t: Ms[];
  series: { key: string; label: string; colorSlot?: number; role: 'primary' | 'compare' | 'projection'
    | 'projection_low' | 'projection_high' | 'target' | 'split'; values: (number | null)[] }[];
  markers: { at: Ms; kind: 'budget' | 'status' | 'creative' | 'alert' | 'checkpoint'; label: string; refId?: Id }[];
  target?: { value: number; label: string };
  freshness: Freshness[];
}

// ---------- Decisões ----------
export type RuleId = `R${string}`;            // 'R01'..'R43'
export type Severity = 'P0' | 'P1' | 'P2' | 'P3';
export type DecisionKind = 'risk' | 'opportunity' | 'info';
export type Confidence = 'high' | 'medium' | 'low';
export type RuleCategory = 'midia_cortar' | 'midia_escalar' | 'criativo' | 'leilao' | 'funil' | 'pagamento'
  | 'ritmo' | 'dados';
export type RuleScope = 'account' | 'campaign' | 'adset' | 'ad' | 'funnel' | 'quiz_question' | 'payment'
  | 'recovery_item' | 'day';
export type ActionKind = 'pause' | 'activate' | 'budget_change' | 'creative_brief' | 'contact' | 'investigate'
  | 'hold_scaling' | 'acknowledge';
export type Cadence = 'event' | '1m' | '5m' | 'checkpoint';

export interface RuleDef {
  id: RuleId; name: string; description: string; category: RuleCategory; scope: RuleScope;
  severity: Severity; escalation?: { to: Severity; when: string }; kind: DecisionKind;
  owners: OwnerTag[]; cadence: Cadence; persistence: { evaluations: number; minGapMin: number };
  params: Record<string, { value: number; default: number; unit: 'x' | 'pct' | 'pp' | 'brl' | 'min' | 'h' | 'n';
    label: string; min: number; max: number; step: number }>;
  minSample: { metric: string; n: number }[];
  cooldownMin: number; actionTemplate: ActionKind | null; impactFormula: string;  // legível
  enabled: boolean;
}
export interface Evidence {
  kpi?: KpiId; label: string; value: number; format: KpiFormat;
  baseline?: number; threshold?: number; window: 'today' | 'last3h' | 'last72h' | 'last7d' | 'last30d' | 'month';
  source: DataSource; status?: KpiStatus;
}
export interface DecisionAction {
  kind: ActionKind;
  target?: { type: 'campaign' | 'adset' | 'ad'; id: Id; name: string };
  params?: { budgetFrom?: Cents; budgetTo?: Cents; deltaPct?: number; brief?: string; checklist?: string[] };
  executable: boolean;                        // true só para pause/activate/budget_change com ads:act
  guardrails: { id: string; label: string; ok: boolean; blocking: boolean }[];
}
export type DecisionStatus = 'new' | 'seen' | 'accepted' | 'executing' | 'executed' | 'failed' | 'snoozed'
  | 'dismissed' | 'resolved' | 'expired';
export interface DecisionEvent { at: Ms; by: Id | 'system'; from: DecisionStatus; to: DecisionStatus; note?: string }
export interface Decision {
  id: Id; ruleId: RuleId; dedupKey: string;                // `${ruleId}:${targetId}:${dateKey}`
  severity: Severity; kind: DecisionKind; confidence: Confidence; falseAlarmP: number;
  title: string; rationale: string; whyText: string;       // pt-BR
  target?: { type: RuleScope; id: Id; name: string; route: string };
  evidence: Evidence[]; owners: OwnerTag[];
  impact: { cents: Cents; label: string; calc: string;     // "≈ R$ 185 economizados hoje"
    horizon?: ImpactHorizon };                             // unidade de tempo do valor (DEC-04 soma só o que é de hoje)
  priorityScore: number;
  action: DecisionAction | null;
  status: DecisionStatus; snoozedUntil?: Ms; dismissReason?: 'resolved_already' | 'false_alarm' | 'strategic' | 'other';
  createdAt: Ms; updatedAt: Ms; escalatedAt?: Ms; seenAt?: Ms;
  /** Última vez que a fonte ainda via o problema (alerta da automação, regra de mídia). Opcional. */
  lastSeenAt?: Ms;
  measured?: { at: Ms; cents: Cents; label: string } | null;
  history: DecisionEvent[];
  suppressedBy?: RuleId[];
}
/** Horizonte do impacto: hoje (já no dia), por dia, por hora, por semana, em 30 dias ou uma vez. */
export type ImpactHorizon = 'today' | 'day' | 'hour' | 'week' | 'month' | 'once';
export type DecisionCommand =
  | { kind: 'seen'; id: Id }
  | { kind: 'apply'; id: Id; note?: string }               // executa action (se executable) ou marca feito
  | { kind: 'snooze'; id: Id; until: Ms }
  | { kind: 'dismiss'; id: Id; reason: Decision['dismissReason']; note?: string }
  | { kind: 'undo'; id: Id };                               // até 10 s após apply
export type AdCommand =
  | { kind: 'set_status'; target: { type: 'campaign' | 'adset' | 'ad'; id: Id }; status: 'ACTIVE' | 'PAUSED'; note?: string }
  | { kind: 'set_budget'; target: { type: 'campaign' | 'adset'; id: Id }; dailyBudget: Cents; note?: string }
  | { kind: 'cancel'; commandId: Id };                     // "Desfazer" antes do envio (6.9)
export interface AdCommandResult { ok: boolean; guardrails: DecisionAction['guardrails'];
  commandId?: Id; sendAt?: Ms;                             // envio adiado de 10 s
  error?: { code: string; message: string } }

// ---------- Briefings ----------
export interface Briefing {
  id: Id; at: Ms; kind: 'checkpoint' | 'weekly' | 'expert'; label: string;   // "Briefing das 15:00"
  headline: string; numbers: { kpi: KpiId; value: number; delta: number | null }[];
  pace: string; changes: string[]; actions: { decisionId: Id; title: string; impactLabel: string }[];
  byRole: { owner: OwnerTag; lines: string[] }[];
  plainText: string;                         // [Copiar texto]: texto puro, sem markdown (critério 33)
  /** ADR-004: o mesmo texto com o `*negrito*` do WhatsApp nos números principais ([Copiar para WhatsApp]). */
  whatsappText?: string;
}

// ---------- Atividade (feed) ----------
export type ActivityKind = 'sale' | 'upsell' | 'pix_created' | 'boleto_created' | 'refused' | 'cart' | 'refund'
  | 'chargeback' | 'cart_recovered' | 'decision' | 'action' | 'source'
  /** Venda da consulta Premium (US$ 397): ícone próprio, nunca o do 20X. */
  | 'premium';
export interface ActivityItem {
  id: Id; at: Ms; kind: ActivityKind; title: string; subtitle: string;
  value?: Cents; refRoute?: string; severity?: Severity;
}

// ---------- Rastreamento ----------
export interface TrackedEventRow {
  id: Id; type: FunnelEventType; source: DataSource; occurredAt: Ms; receivedAt: Ms; latencyMs: number;
  dedupKey: string; status: 'saved' | 'duplicate' | 'dropped'; dropReason?: string;
  /** ADR-002: sessão do funil (sid) ligada ao evento; null quando o evento chegou sem sessão. */
  sck?: string | null;
}
export interface UntrackedSale { orderId: Id; at: Ms; product: ProductKind; value: Cents;
  reason: NonNullable<AttributionRef['reason']>; tracking: TrackingParams }
export interface UtmDiagnostic { adId: Id; adName: string; problem: 'missing' | 'no_ids' | 'custom'; current: string; suggested: string }
export interface TrackingHealth {
  overall: 'healthy' | 'attention' | 'critical'; sentence: string;
  indicators: { kpi: KpiId; value: number | null; status: KpiStatus }[];
  metaPlatformStatus: 'ok' | 'degraded' | 'outage';
}

// ---------- Financeiro ----------
export interface PnL {
  lines: { id: 'gross' | 'fee' | 'tax' | 'refunds' | 'media' | 'variable' | 'contribution' | 'fixed' | 'operating';
    label: string; cents: Cents; subtotal: boolean }[];
  margin: Rate | null; mer: number | null; basis: 'event_date' | 'cohort';
  /** De onde vêm os números: pedidos reais (modo ao vivo) ou simulação. Opcional. */
  source?: 'real' | 'sim';
}
export interface UnitEconomics { u: Rate; r: Rate; m: Rate; t: Rate; measuredWindowDays: number;
  cpaBreakEven: Cents; cpaTarget: Cents; roasBreakEven: number; roasTarget: number;
  contributionPerBuyer: Cents /* C */; cpiTarget: Cents; roasRealTarget: number; merBreakEven: number; merTarget: number;
  roiTarget: Rate; rpvBreakEven: Cents; checkoutConv7d: Rate;
  defaults: { cpaBreakEven: Cents; cpaTarget: Cents; roasBreakEven: number; roasTarget: number } }
/** ADR-002: base da DRE. `event_date` (padrão) conta cada evento no dia em que aconteceu; `cohort` olha as
 *  vendas do período e o que aconteceu com elas depois (reembolsos e chargebacks da própria coorte). */
export type FinanceBasis = 'event_date' | 'cohort';
export interface FinanceQuery extends Query { basis?: FinanceBasis }
export interface FinanceReport {
  pnl: PnL; unit: UnitEconomics;
  cash: { available: Cents; pending: Cents; runwayDays: number | null; calendar: { dateKey: DateKey; cents: Cents }[] };
  /** ADR-002: a mesma DRE na janela de comparação (`q.compare`), quando há dados para ela. */
  compare?: PnL | null;
}
export interface Expense { id: Id; name: string; category: 'trafego' | 'ferramentas' | 'funcionarios' | 'contabilidade'
  | 'expert' | 'outros'; amount: Cents; recurrence: 'once' | 'monthly'; startsOn: DateKey; dailyProrated: Cents }

// ---------- Atribuição manual e sincronização (ADR-002) ----------
export interface ManualAttributionResult {
  orders: Order[];
  /** Pedidos que não puderam ser atribuídos (não encontrado, ou sem permissão sobre eles). */
  failed: { orderId: Id; code: ProviderError['code']; message: string }[];
}

// ---------- Configurações ----------
export interface Settings {
  goals: { monthlyRevenue: Cents; targetMargin: Rate; dailySpendCap: Cents; weekdayWeights: number[] /* dom..sáb */ };
  costs: { kiwifyFeePct: Rate; kiwifyFeeFixed: Cents; metaTaxPct: Rate; revenueTaxPct: Rate; variablePerSale: Cents;
    expenses: Expense[] };
  economics: { priceFront: Cents; priceUpsell: Cents; refundRateDefault: Rate; upsellTakeDefault: Rate;
    pitchSec: number; upsellPitchSec: number;
    productIds: { front: Id[]; upsell: Id[] };            // classifica pedidos Kiwify (curso × 20X) pelo product_id
    refundFeeRetained: boolean };                          // taxa Kiwify não volta no reembolso (padrão true, a confirmar)
  meta: { accountIds: Id[]; pixelId: string };           // várias contas de anúncio
  thresholds: Partial<Record<KpiId, ThresholdSpec>>;      // overrides
  rules: Partial<Record<RuleId, { enabled?: boolean; params?: Record<string, number> }>>;
  recovery: { dueMin: Record<RecoveryType, number>; pRecovery: Record<RecoveryType, Rate>;
    kiwifyAgentActive: boolean; humanAfterAgentMin: number /* 60 */; mergeWindowH: number /* 24 */ };
  notifications: { saleToasts: boolean; showValue: boolean; sound: boolean; briefings: string[] /* "09:00" */ };
  ui: { singleKeyShortcuts: boolean };                   // WCAG 2.1.4 (por usuário; V1 em localStorage)
  checkpoints: string[];                                  // ["09:00","12:00","15:00","18:00","21:00","23:30"]
}
export type SettingsPatch = { [K in keyof Settings]?: Partial<Settings[K]> };

// ---------- Snapshot ----------
export interface DashboardSnapshot {
  query: Query; generatedAt: Ms; now: Ms;
  kpis: Partial<Record<KpiId, KpiValue>>;
  goals: GoalProgress | null;
  funnel: FunnelStep[];
  headline: string;                         // frase-resumo do HOJ-01
  openDecisions: { p0: number; p1: number; p2: number; p3: number };
  recoverySummary: { open: number; dueSoon: number; overdue: number; moneyAtStake: Cents;
    /** Itens em aberto por tipo. Opcional. */
    byType?: Record<RecoveryType, number>;
    /** Criação do item em aberto mais antigo (null = fila vazia). Opcional. */
    oldestAt?: Ms | null };
  tracking: TrackingHealth;
  freshness: Freshness[];
}

// ---------- Boot ----------
export interface BootPayload {
  viewer: Viewer; serverNow: Ms; timezone: 'America/Sao_Paulo'; contractVersion: typeof CONTRACT_VERSION;
  settings: Settings; kpiDefs: Record<KpiId, KpiDef>; ruleDefs: RuleDef[];
  quiz: QuizQuestion[]; players: VslPlayer[];
  accounts: AdAccount[]; campaigns: Campaign[]; adsets: AdSet[]; ads: Ad[];
  integrations: IntegrationStatus[]; users: { id: Id; name: string; initials: string; role: Role }[];
  lastVisitAt: Ms | null;                   // para o sheet "O que mudou" (3.9)
}

/** "O que mudou desde sua última visita" (3.9). Já filtrado pelo papel. */
export interface ChangesSince {
  since: Ms; sales: number; revenueGross: Cents; newDecisions: number; autoResolved: number;
  actionsByOthers: { at: Ms; by: string; label: string }[];
  outages: { source: IntegrationSource; from: Ms; to: Ms | null }[];
}

// ---------- Listas paginadas ----------
export type ListKind = 'orders' | 'recovery' | 'activity' | 'decisions' | 'refunds' | 'untracked'
  | 'events' | 'utmDiagnostics' | 'briefings' | 'audit' | 'creatives' | 'vslSources';
export interface ListRowMap {
  orders: Order; recovery: RecoveryItem; activity: ActivityItem; decisions: Decision; refunds: Refund;
  untracked: UntrackedSale; events: TrackedEventRow; utmDiagnostics: UtmDiagnostic; briefings: Briefing;
  audit: AuditEntry; creatives: InsightRow; vslSources: VslSourceRow;
}
export interface ListQuery extends Query {
  status?: string[]; types?: string[]; search?: string; assignee?: Id | 'me' | 'none';
  sort?: { key: string; dir: 'asc' | 'desc' }; cursor?: string; limit: number;   // limit ≤ 200
}
export interface Page<T> { rows: T[]; nextCursor: string | null; total: number }
export interface AuditEntry { id: Id; at: Ms; actorId: Id; action: 'pii_reveal' | 'decision' | 'ad_command'
  | 'settings' | 'export' | 'manual_attribution' | 'role_view'; detail: string }

// ---------- Eventos brutos do funil (tracker + webhooks normalizados) ----------
export type FunnelEventType =
  | 'quiz.view' | 'quiz.start' | 'quiz.answer' | 'quiz.complete' | 'quiz.result_view' | 'quiz.cta_vsl'
  | 'vsl.view' | 'vsl.play' | 'vsl.progress' | 'vsl.pitch_reached' | 'vsl.offer_revealed' | 'vsl.cta_click'
  | 'checkout.redirect'
  | 'upsell.view' | 'upsell.play' | 'upsell.pitch_reached' | 'upsell.cta_click' | 'upsell.declined_inferred'
  | 'order.pix_created' | 'order.boleto_created' | 'order.approved' | 'order.refused' | 'order.refunded'
  | 'order.chargeback' | 'cart.abandoned' | 'cart.recovered'
  | 'meta.status_changed' | 'meta.threshold_crossed' | 'meta.insights_refreshed'
  | 'vturb.stats_refreshed' | 'action.executed';
export interface FunnelEvent<P = Record<string, unknown>> {
  id: Id; dedupKey: string; type: FunnelEventType; source: DataSource;
  occurredAt: Ms; receivedAt: Ms;
  sessionId?: Id; orderId?: Id; cartId?: Id;
  metaObject?: { type: 'campaign' | 'adset' | 'ad'; id: Id };
  tracking?: TrackingParams; attribution?: AttributionRef; value?: Cents; device?: Device;
  payload: P;   // vsl.progress → { second }; quiz.answer → { questionId, optionId, step }
}

// ---------- Tempo real ----------
export type Topic = 'kpis' | 'activity' | 'orders' | 'recovery' | 'decisions' | 'vsl' | 'insights'
  | 'sources' | 'events' | 'briefings';
interface LiveBase { v: typeof CONTRACT_VERSION; seq: number; ts: Ms }
export type LiveMessage = LiveBase & (
  | { type: 'kpi.patch'; topic: 'kpis'; data: { kpis: Partial<Record<KpiId, KpiValue>>; goals?: GoalProgress; headline?: string } }
  | { type: 'activity.new'; topic: 'activity'; data: ActivityItem }
  | { type: 'order.upsert'; topic: 'orders'; data: Order }
  | { type: 'recovery.upsert'; topic: 'recovery'; data: RecoveryItem }
  | { type: 'decision.upsert'; topic: 'decisions'; data: Decision }
  | { type: 'vsl.live'; topic: 'vsl'; data: { playerId: Id; viewers: number; pitchReachedToday: number } }
  | { type: 'insights.refreshed'; topic: 'insights'; data: { level: InsightLevel; at: Ms } }  // UI refaz getInsights
  | { type: 'source.status'; topic: 'sources'; data: IntegrationStatus }
  | { type: 'event.new'; topic: 'events'; data: TrackedEventRow }
  | { type: 'briefing.created'; topic: 'briefings'; data: Briefing }
  | { type: 'entity.updated'; topic: 'insights'; data: { type: 'campaign' | 'adset' | 'ad'; id: Id; patch: { status?: MetaConfiguredStatus; effectiveStatus?: MetaEffectiveStatus; dailyBudget?: Cents | null; lastEditAt?: Ms; budgetChangesLastHour?: number; edits?: EditLogEntry[] } } }
  | { type: 'resync'; topic: 'kpis'; data: { reason: 'gap' | 'version' | 'day_rollover' } }   // UI refaz snapshot
);

// ---------- Simulação (somente MockProvider) ----------
export type ScenarioId = 'normal' | 'recorde' | 'checkout_caiu' | 'cartao_caiu' | 'pagina_lenta'
  | 'webhook_silencioso' | 'criativo_fadigado' | 'upsell_quebrado' | 'madrugada_vazia';
export interface SimState { scenario: ScenarioId; speed: 1 | 10 | 60; clock: { mode: 'real' | 'fixed'; fixedAt?: string /* HH:MM */ };
  seed: string; integrations: Record<IntegrationSource, IntegrationState | 'stale'>; simNow: Ms }
export interface SimControl {
  get(): Promise<SimState>;
  set(patch: Partial<Omit<SimState, 'simNow'>>): Promise<SimState>;   // reconstrói o dia determinísticamente
  resetDay(): Promise<void>;
}

// ---------- O provider ----------
export interface DataProvider {
  readonly kind: 'mock' | 'api';
  init(viewerId: Id): Promise<BootPayload>;
  switchViewer(role: Role): Promise<BootPayload>;                         // "Ver como" (owner)
  getSnapshot(q: Query): Promise<DashboardSnapshot>;
  getSeries(q: SeriesQuery): Promise<ColumnarSeries>;
  getFunnel(q: Query): Promise<FunnelReport>;
  getInsights(q: InsightQuery): Promise<InsightTable>;
  getQuiz(q: Query): Promise<QuizReport>;
  getSegments(q: SegmentQuery): Promise<SegmentReport>;
  getRetention(q: RetentionQuery): Promise<{ curves: RetentionCurve[]; milestones: { second: number; pct: Rate; delta: number | null }[]; abTest: AbTest | null }>;
  getFinance(q: FinanceQuery): Promise<FinanceReport>;
  getTrackingHealth(q: Query): Promise<TrackingHealth>;
  getOrderJourney(orderId: Id): Promise<OrderJourney | null>;
  getDecision(id: Id): Promise<Decision | null>;
  getKpiDetail(q: Query & { kpi: KpiId }): Promise<{ value: KpiValue; series: ColumnarSeries; byCampaign: { id: Id; name: string; value: number | null }[] }>;
  getChangesSince(since: Ms): Promise<ChangesSince>;                      // sheet "O que mudou" (3.9)
  list<K extends ListKind>(kind: K, q: ListQuery): Promise<Page<ListRowMap[K]>>;
  status(): Promise<IntegrationStatus[]>;
  subscribe(topics: Topic[], onMessage: (m: LiveMessage) => void, opts?: { lastSeq?: number }): Unsubscribe;
  decide(cmd: DecisionCommand): Promise<Decision>;
  adCommand(cmd: AdCommand, opts: { dryRun: boolean }): Promise<AdCommandResult>;   // dryRun → só guardrails
  recovery(cmd: RecoveryCommand): Promise<RecoveryItem>;
  revealPii(ref: { kind: 'order' | 'cart' | 'recovery'; id: Id }): Promise<CustomerPii>;  // audita
  attributeManually(orderId: Id, target: { type: 'campaign' | 'adset' | 'ad'; id: Id }): Promise<Order>;
  updateSettings(patch: SettingsPatch): Promise<Settings>;
  simulateRule(ruleId: RuleId, params: Record<string, number>, days: 7): Promise<{ fired: number; targets: { name: string; at: Ms }[] }>;
  sim?: SimControl;
  // ---- ADR-002 (opcionais: a UI testa a existência antes de chamar) ----
  /** EXP-03: objeções da recuperação agregadas para a Pauta, sem dado pessoal. */
  getPautaObjections?(q: Query): Promise<PautaObjections>;
  /** Sincroniza uma fonte só (Rastreamento/Ajustes › Integrações). Emite `source.status` e, se mudou dado, `resync`. */
  syncSource?(source: IntegrationSource): Promise<IntegrationStatus>;
  /** Atribuição manual em lote (≤ 200 pedidos): um comando, uma auditoria por pedido, um `resync` no fim. */
  attributeManuallyBatch?(orderIds: Id[], target: { type: 'campaign' | 'adset' | 'ad'; id: Id }): Promise<ManualAttributionResult>;
}

// ---------- Contexto das regras (packages/rules) ----------
export type Window = 'today' | 'last3h' | 'last72h' | 'last7d' | 'last30d';
export interface ScopeMetrics { id: Id; type: RuleScope; name: string; byWindow: Partial<Record<Window, Partial<Record<KpiId | string, number | null>>>>;
  baseline: Partial<Record<KpiId | string, number | null>> }
export interface RuleContext {
  now: Ms; dateKey: DateKey; hourSP: number; minuteOfDay: number; fNow: Rate; gNow: Rate;
  params: UnitEconomics & { pitchSec: number };
  account: ScopeMetrics /* soma de todas as contas */; accounts: ScopeMetrics[]; campaigns: ScopeMetrics[]; adsets: ScopeMetrics[]; ads: ScopeMetrics[];
  funnel: ScopeMetrics; payments: ScopeMetrics; quizSteps: QuizStepStat[];
  entities: { campaigns: Campaign[]; adsets: AdSet[]; ads: Ad[]; accounts: AdAccount[] };
  dataQuality: { trackerOk: boolean; metaFresh: boolean; webhookSilent: boolean; utmCoverageBad: boolean;
    sckCoverageBad: boolean; mediaRulesLocked: boolean };   // trava de dados (6.3, item 9)
  recovery: RecoveryItem[]; recentEvents: FunnelEvent[]; integrations: IntegrationStatus[];
  goals: GoalProgress | null; openDecisions: Decision[]; settings: Settings;
}
export interface Finding {
  ruleId: RuleId; targetId: Id; target?: Decision['target']; severity: Severity; kind: DecisionKind;
  confidence: Confidence; falseAlarmP: number; title: string; rationale: string; whyText: string;
  evidence: Evidence[]; impactCents: Cents; impactLabel: string; impactCalc: string;
  action: Omit<DecisionAction, 'guardrails' | 'executable'> | null;
}
export type RulePredicate = (ctx: RuleContext, params: Record<string, number>) => Finding[];
export * from './automacaoDash.ts';
export * from './palco.ts';
export * from './v2.ts';
